import * as $ from "jquery";
import * as _ from "lodash";
import {BaseSensorConfigView, BaseSensorConfigViewOptions} from "./_BaseSensorConfigView";
import {ChannelRecord} from "../../../models/Module";

export interface LayoutConfig {
    sensorName: string,
    fields: LayoutConfigField[],
}

interface LayoutConfigField {
    type: 'toggle' | 'dropdown' | 'hidden' | 'tempOffset' | 'tempOffset_dropdown';
    configNames?: string[];
    label?: string;
    inverted?: boolean;
    options?: LayoutConfigFieldOption[];
    ifAnyPositionsPresent?: number[];
}

interface LayoutConfigFieldOption {
    value: number;
    label: string;
}

export interface GenericConfigViewOptions extends BaseSensorConfigViewOptions {
    layoutConfig: LayoutConfig,
    canDeleteChannel?: boolean,
}

export class GenericConfigView extends BaseSensorConfigView {

    options: GenericConfigViewOptions;
    private layoutConfig: LayoutConfig;
    private connector: number;
    private channels: ChannelRecord[];
    private canEdit: boolean;
    private template = _.template(require('templates/settings/sensor_config/generic.html'));
    private tempOffsetTemplate = _.template(require('templates/settings/sensor_config/tempOffset.html'));

    constructor(options: GenericConfigViewOptions) {
        super(<any> _.extend(options, {
            el: '',
            events: {
                'click .delete-channel': 'deleteChannelRow',
            }
        }));
        this.layoutConfig = options.layoutConfig;
        this.connector = this.con.sensor_connector;
        this.channels = _.sortBy(this.con['channels'], (c) => {
            return c['channel_position']
        });
        this.canEdit = this.permissionCollection.canModifySensorConfig(this.installId);
    };

    private deleteChannelRow(e: JQuery.UIEventBase) {
        var div = $(e.currentTarget).parents('.channel-container');
        div.remove();
    }

    private getValueFromConfigValue(configNames) {
        var val;
        _.each(configNames, (configName, i) => {
            var j = Number(i);
            var configVal = this.config.findWhere({type: configName}).get('value');
            if (j > 0) configVal = configVal << (16 * j);
            val = val | configVal;
        });
        return val;
    }

    private getTempOffsetValue() {
        return new Promise((resolve, reject) => {
            const offsetH = this.config.findWhere({ type: 'TEMP_OFFSET_H' }).get('value');
            const offsetL = this.config.findWhere({ type: 'TEMP_OFFSET_L' }).get('value');

            $.ajax({
                type: 'GET',
                url: `${window.location.origin}/api/v1/bits/numericvalues?uint16=${offsetH},${offsetL}`,
                dataType: 'json',
                contentType: "application/json; charset=utf-8",
                success: (response) => {
                    resolve(response.float32[0]);
                    return;
                },
                error: function (err) {
                    reject(err);
                    return;
                }
            });
        });
    }

    private getFormGroup(label: string, input: string) {
        return '<div class="form-group' + ( label === null ? ' hidden' : '' ) + '">' +
            '<label class="col-sm-3 control-label">' + ( label === null ? '' : label ) + '</label>' +
            '<div class="col-sm-9">' + input + '</div></div>';
    }

    private getToggleButton(fieldDefinition: LayoutConfigField) {
        var label = fieldDefinition.label;
        var configName = fieldDefinition.configNames![0];
        var checked = Boolean(this.config.findWhere({type: configName}).get('value'));
        
        if (fieldDefinition.inverted) checked = !checked;
        
        var input = '<input type="checkbox" data-toggle="toggle" data-size="small" ' + (checked ? 'checked' : '' )
            + ' data-config-name="' + configName + '" ' + (this.canEdit ? '' : ' disabled') + ' />';
        return this.getFormGroup(label, input);
    }

    private getDropDown(fieldDefinition: LayoutConfigField) {
        var label = fieldDefinition.label;
        var currentValue = this.getValueFromConfigValue(fieldDefinition.configNames!);
        var select = '<select class="form-control settings-sensorconfig-field" data-config-name="' +
            fieldDefinition.configNames![0] + '" ' + (this.canEdit ? '' : ' disabled') + ' >';
        _.each(fieldDefinition.options, (option) => {
            var selected = option.value === currentValue ? ' selected' : '';
            select += '<option value ="' + option.value + '"' + selected + '>' + option.label + '</option>';
        });
        select += '</select>';
        return this.getFormGroup(label, select);
    }

    private getHiddenField(fieldDefinition: LayoutConfigField) {
        var currentValue = this.getValueFromConfigValue(fieldDefinition.configNames!);
        var input = '<input type="hidden" value="' + currentValue + '" data-config-name="' +
            fieldDefinition.configNames![0] + '" ' + (this.canEdit ? '' : ' disabled') + ' />';
        return this.getFormGroup(null, input);
    }


    private async getTempOffsetFields() {
        // Temperature Offset value
        const tempOffsetValue = await this.getTempOffsetValue();
        //Temperature Unit value
        const unitValue = this.config.findWhere({ type: 'TEMP_OFFSET_UNIT' }).get('value');

        return <string>this.tempOffsetTemplate({
            canEdit: this.canEdit,
            tempOffsetValue: tempOffsetValue,
            unit: unitValue
        });
    }

    private isPositionPresent(position: number): boolean {
        return Boolean(_.find(this.channels, { channel_position: position }));
    };

    render() {
        var hasVisibleFields = false;
        var fields = _.map(this.layoutConfig.fields, (fieldDefinition: LayoutConfigField) => {
            // First check if we want to show the field based on positions
            if (_.size(fieldDefinition.ifAnyPositionsPresent)) {
                if (!_.some(_.map(fieldDefinition.ifAnyPositionsPresent, this.isPositionPresent.bind(this)))) {
                    return this.getHiddenField(fieldDefinition);
                }
            }
            // Otherwise return a field based on the field definition type
            if (fieldDefinition.type === 'dropdown') {
                hasVisibleFields = true;
                return this.getDropDown(fieldDefinition);
            } else if (fieldDefinition.type === 'toggle') {
                hasVisibleFields = true;
                return this.getToggleButton(fieldDefinition);
            } else if (fieldDefinition.type === 'hidden') {
                return this.getHiddenField(fieldDefinition);
            } else if (fieldDefinition.type === 'tempOffset') {
                hasVisibleFields = true;
                return this.getTempOffsetFields();
            }
        });

        Promise.all(fields).then((fields) => {
            this.$el.html(this.template({
                layoutConfig: this.layoutConfig,
                configFields: fields.join(''),
                hasVisibleFields: hasVisibleFields,
                connector: this.connector,
                con: this.con,
                channels: this.channels,
                module: this.module,
                moduleNumber: Number(this.module.get('module_number')),
                permissionCollection: this.permissionCollection,
                conversionFunctionCollection: this.conversionFunctionCollection,
                channelParamCollection: this.channelParamCollection,
                installId: this.installId,
                canDeleteChannel: this.options.canDeleteChannel
            }));

            // Initialize bootstrap toggles
            (<any>this.$('[data-toggle="toggle"]')).bootstrapToggle();
        });
        return this;
    }

    hasUpdatedConfig() {
        return true;
    }

    validate() {
        return true;
    }

    private getConfigValueFromUI(fieldDefinition: LayoutConfigField, configName: string) {
        var elem = this.$('[data-config-name="' + _.first(fieldDefinition.configNames!) + '"]');
        var tag = elem.get(0).nodeName;
        if (tag === 'SELECT') {
            var value = Number(elem.val());
            var index = _.indexOf(fieldDefinition.configNames!, configName);
            if (index === 0) {
                return value % (1 << 16)
            } else {
                return Math.floor(value / (1 << (16 * index)))
            }
        } else if (tag === 'INPUT') {
            if (elem.attr('type') === 'checkbox') {
                var checked = elem.is(':checked');
                return fieldDefinition.inverted ? Number(!checked) : Number(checked);
            } else {
                return Number(elem.val());
            }
        }
    }


    getConfig() : any {
        return new Promise((resolve, reject) => {
            const rtn = [];
            this.config.each((c) => {
                const type = c.get('type');
                let value = c.get('value');
                _.each(this.layoutConfig.fields, (f) => {
                    if (f.configNames && _.includes(f.configNames, type)) value = this.getConfigValueFromUI(f, type);
                });
                rtn.push({ sensor_id: this.sensorId, type: type, value: value })
            });


            const sensorVersion = this.con['sensor_version'];
            const sensorType = this.con['sensor_type'];
            if (sensorType !== 34 || sensorVersion < 3) {
                resolve(rtn);
                return;
            }

            const elem = this.$('[data-config-name="TEMP_OFFSET"]');
            const value = Number(elem.val());
            // Reject if the temperature offset value is not between -10 and 10
            if (value < -10 || value > 10) reject('TEMP_OFFSET_VALUE');

            $.ajax({
                type: 'GET',
                url: `${window.location.origin}/api/v1/bits/numericvalues?float32=${value}`,
                dataType: 'json',
                contentType: "application/json; charset=utf-8",
                success: (response) => {
                    // Use the unit16be to populate the temperature ofset parameters
                    const offsetH = rtn.find(element => element.type === 'TEMP_OFFSET_H');
                    const offsetL = rtn.find(element => element.type === 'TEMP_OFFSET_L');

                    if (offsetH) offsetH.value = response.uint16be[0];
                    if (offsetL) offsetL.value = response.uint16be[1];

                    resolve(rtn);
                    return;
                },
                error: function (err) {
                    reject(err);
                    return;
                }
            });
        });
    }

    static channelPropertiesMatch(channel, name, unit, convfunc, dec) {
        return (name !== channel['channel_name']
        || unit !== channel['channel_unit']
        || convfunc !== channel['channel_conversion_function_id']
        || dec !== channel['channel_decimals'])
    }


    getUpdatedChannels() {
        var update = {};
        var deleteIds = [];
        _.each(this.channels, (channel) => {
            var dec, convfunc;
            var id = channel['channel_id'];
            var nameDiv = (<string> this.$('.settings-sensorconfig-channel-name[data-channel-id="' + id + '"]').val());
            //assume if this div dne, that the 'deleteChannel' option has been selected (for kits)
            if(!nameDiv) {
                deleteIds.push(id);
                return;
            }
            var name = (<string> this.$('.settings-sensorconfig-channel-name[data-channel-id="' + id + '"]').val())
                .trim();
            var unit = (<string> this.$('.settings-sensorconfig-channel-unit[data-channel-id="' + id + '"]').val())
                .trim();
            var cf = this.$('.settings-sensorconfig-channel-conversion-function[data-channel-id="' + id + '"]');
            var decimals = this.$('.settings-sensorconfig-channel-decimal-digits[data-channel-id="' + id + '"]');
            if (!unit.length) {
                unit = null;
            }
            if (cf.length) {
                var val = Number(cf.val());
                convfunc = val === -1 ? null : val;
            } else {
                convfunc = null;
            }
            if (decimals.length) {
                var val = Number(decimals.val());
                dec = val === -1 ? null : val;
            } else {
                dec = channel['channel_decimals'];
            }
            if (GenericConfigView.channelPropertiesMatch(channel, name, unit, convfunc, dec)) {
                update[id] = {
                    id: id,
                    name: name,
                    unit: unit,
                    decimals: dec,
                    conversion_function_id: convfunc,
                    position: channel['channel_position']
                }
            }
        });
        // For generic sensors there should never be a channel to delete or create
        return {create: [], update: update, 'delete': deleteIds};
    }

    getUpdatedChannelParams() {
        var update = {};
        _.each(this.$('.settings-sensorconfig-channel-param'), (param) => {
            var p = $(param);
            var id = Number(p.attr('data-param-id'));
            var value = Number(p.val());
            if (this.channelParamCollection.findWhere({id: id}).get('value') !== value) {
                update[id] = {
                    id: id,
                    value: value
                };
            }
        });
        return {create: [], update: update, 'delete': []};
    }


}



// WEBPACK FOOTER //
// ./srv/public/js/views/settings/sensor_config/GenericConfigView.ts